import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from './lib/firebase';
import './App.css';
import Header from './components/Header';
import MiniNavbar from './components/MiniNavbar';
import Hero from './components/Hero';
import About from './components/About';
import Works from './components/Works';
import Process from './components/Process';
import Testimonials from './components/Testimonials';
import CTA from './components/CTA';
import Footer from './components/Footer';
import ContactForm from './components/ContactForm';
import EventPopupModal from './components/EventPopupModal';
import ContactPage from './pages/ContactPage';
import WorksPage from './pages/WorksPage';
import StudioPage from './pages/StudioPage';
import ProcessPage from './pages/ProcessPage';
import Menu from './components/Menu';
import { initScripts, cleanupScripts } from './utils/initScripts';

// Import admin components1
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminPortfolios from './pages/admin/AdminPortfolios';
import AdminEvents from './pages/admin/AdminEvents';
import AdminReviews from './pages/admin/AdminReviews';
import AdminContacts from './pages/admin/AdminContacts';
import AdminSettings from './pages/admin/AdminSettings';
import AdminHistory from './pages/admin/AdminHistory';
import AdminLoginPage from './pages/admin/AdminLoginPage';

import WorksHeader from './components/WorksHeader';
import WorksMiniNavbar from './components/WorksMiniNavbar';

import ProjectDetailPage from './pages/ProjectDetailPage';
import EventDetailPage from './pages/EventDetailPage';

// Import the slater.js functions for menu and animation functionality
import { initScript } from './assets/js/slater';

function App() {
  const [showContactForm, setShowContactForm] = useState(false);
  const location = useLocation();
  const [popupEvent, setPopupEvent] = useState(null);
  const [isPopupVisible, setIsPopupVisible] = useState(false);

  // Check for active popup events on initial load
  useEffect(() => {
    const checkForPopupEvent = async () => {
      // Check if user has closed the popup today
      const lastClosedDate = localStorage.getItem('eventPopupClosed');
      const today = new Date().toISOString().split('T')[0];
      if (lastClosedDate === today) {
        return;
      }

      // Fetch active events marked as popups
      const eventsRef = collection(db, 'events');
      const q = query(eventsRef, where('is_active', '==', true), where('is_popup', '==', true));
      
      try {
        const querySnapshot = await getDocs(q);
        const now = new Date();
        
        let eventToShow = null;
        querySnapshot.forEach((doc) => {
          const event = { id: doc.id, ...doc.data() };
          const startDate = new Date(event.start_date);
          const endDate = new Date(event.end_date);
          // Set end of day for comparison
          endDate.setHours(23, 59, 59, 999);

          if (now >= startDate && now <= endDate) {
            // Found a valid event, select the first one
            if (!eventToShow) {
              eventToShow = event;
            }
          }
        });

        if (eventToShow) {
          setPopupEvent(eventToShow);
          setIsPopupVisible(true);
        }
      } catch (error) {
        console.error("Error fetching popup event: ", error);
      }
    };

    // Only run this check on the main page
    if (location.pathname === '/') {
      checkForPopupEvent();
    }
  }, [location.pathname]);

  // Check if we're on an admin page
  const isAdminPage = location.pathname.startsWith('/admin');

  useEffect(() => {
    // Initialize preloader
    // initLoader();
    
    // Initialize scripts after component mount
    initScripts();
    
    // Initialize slater.js scripts
    initScript();
    
    // Add event listener for contact form buttons
    const handleContactBtnClick = (e) => {
      if (e.target.closest('[data-contact-btn]')) {
        setShowContactForm(true);
        document.body.style.overflow = 'hidden'; // Prevent scrolling
      }
    };

    // Add event listener for menu buttons
    const handleMenuBtnClick = (e) => {
      if (e.target.closest('[data-menu-btn]')) {
        document.body.dataset.navigationStatus = 'is-open';
      }
    };

    // Add event listener for menu close buttons
    const handleMenuCloseClick = (e) => {
      if (e.target.closest('[data-menu-close]')) {
        document.body.dataset.navigationStatus = 'is-closed';
      }
    };

    // Add event listener for contact form close button
    const handleContactFormClose = (e) => {
      if (e.target.closest('[data-contact-close]') || e.target.classList.contains('contact-form-overlay')) {
        setShowContactForm(false);
        document.body.style.overflow = ''; // Restore scrolling
      }
    };

    document.addEventListener('click', handleContactBtnClick);
    document.addEventListener('click', handleMenuBtnClick);
    document.addEventListener('click', handleMenuCloseClick);
    document.addEventListener('click', handleContactFormClose);
    
    // Cleanup scripts and event listeners when component unmounts
    return () => {
      cleanupScripts();
      document.removeEventListener('click', handleContactBtnClick);
      document.removeEventListener('click', handleMenuBtnClick);
      document.removeEventListener('click', handleMenuCloseClick);
      document.removeEventListener('click', handleContactFormClose);
    };
  }, []);

  const closeContactForm = () => {
    setShowContactForm(false);
    document.body.style.overflow = ''; // Restore scrolling
  };

  return (
    <div className="App">
      {/* Only render header, navbar, and menu on non-admin pages */}
      {!isAdminPage && (
        <>
          {location.pathname === '/works' || location.pathname === '/studio' || location.pathname === '/process' ? (
            <>
              <WorksHeader />
              <WorksMiniNavbar />
            </>
          ) : (
            <>
              <Header />
              <MiniNavbar />
            </>
          )}
          <Menu />
        </>
      )}
      
      <Routes>
        <Route path="/" element={
          <>
            <main className="page_main">
              <Hero />
              <About />
              <Works />
              <Process />
              <Testimonials />
              <CTA />
            </main>
            <Footer />
            {showContactForm && <ContactForm isOpen={showContactForm} onClose={closeContactForm} />}
          </>
        } />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/works" element={<WorksPage />} />
        <Route path="/studio" element={<StudioPage />} />
        <Route path="/process" element={<ProcessPage />} />
        <Route path="/project/:id" element={<ProjectDetailPage />} />
        <Route path="/event/:id" element={<EventDetailPage />} />
        
        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
<Route path="/admin" element={<AdminLayout />}>
  <Route index element={<AdminDashboard />} />
  <Route path="portfolios" element={<AdminPortfolios />} />
  <Route path="events" element={<AdminEvents />} />
  <Route path="reviews" element={<AdminReviews />} />
  <Route path="contacts" element={<AdminContacts />} />
  <Route path="settings" element={<AdminSettings />} />
  <Route path="history" element={<AdminHistory />} />
</Route>
      </Routes>
      
      {/* Global contact form - always render but conditionally display */}
      {showContactForm && <ContactForm isOpen={showContactForm} onClose={closeContactForm} />}

      {/* Global Event Popup Modal */}
      {isPopupVisible && popupEvent && (
        <EventPopupModal 
          event={popupEvent}
          onClose={() => setIsPopupVisible(false)}
          onDontShowToday={() => {
            localStorage.setItem('eventPopupClosed', new Date().toISOString().split('T')[0]);
            setIsPopupVisible(false);
          }}
        />
      )}
    </div>
  );
}

export default App;
